import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, AlertTriangle } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import NavBar from "@/components/NavBar";
import SupportSubmitModal from "@/components/support/SupportSubmitModal";

export default function MyTicketsPage() {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [showNew, setShowNew] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/support/tickets/my");
      setTickets(data.tickets || []);
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  return (
    <>
      <NavBar />
      <main className="max-w-4xl mx-auto px-5 sm:px-10 py-10" data-testid="my-tickets-page">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="overline">Support</div>
            <h1 className="font-serif text-4xl text-[#1A422D] mt-2">My Support Tickets</h1>
            <p className="mt-2 text-sm text-[#4A4A4A] max-w-xl">
              File a new ticket or track the progress of your open issues.
              Our team will respond in-app and (if you enabled email) to your inbox.
            </p>
          </div>
          <button type="button" onClick={() => setShowNew(true)}
                  data-testid="my-tickets-new"
                  className="inline-flex items-center gap-2 bg-[#1A422D] text-[#F9F8F4] px-4 py-2 text-[11px] uppercase tracking-[0.22em] hover:bg-[#0f1a14]">
            <Plus size={12} /> New Ticket
          </button>
        </div>

        {err && (
          <div className="mt-5 border border-[#A33] bg-[#fbe9e9] text-[#8a1a1a] px-3 py-2 text-xs flex items-center gap-2">
            <AlertTriangle size={12} /> {err}
          </div>
        )}

        <div className="mt-8 surface-card">
          {loading && <div className="p-8 text-sm text-[#7A7A7A]">Loading…</div>}
          {!loading && tickets.length === 0 && (
            <div className="p-10 text-center text-sm text-[#7A7A7A]" data-testid="my-tickets-empty">
              You haven&rsquo;t filed a ticket yet.
            </div>
          )}
          {tickets.length > 0 && (
            <ul className="divide-y divide-[#E5E3D8]" data-testid="my-tickets-list">
              {tickets.map((t) => (
                <li key={t.ticket_id} data-testid={`my-ticket-${t.ticket_id}`}>
                  <Link to={`/support/tickets/${t.ticket_id}`}
                        className="block p-5 hover:bg-[#F9F8F4] transition">
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div className="min-w-0">
                        <div className="font-serif text-lg text-[#1A422D] truncate">{t.subject}</div>
                        <div className="text-[10px] uppercase tracking-[0.2em] text-[#7A7A7A] mt-0.5">
                          {t.ticket_id} · {new Date(t.created_at).toLocaleDateString()}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <span className="text-[10px] uppercase tracking-[0.18em] text-[#7A7A7A]">
                          {t.priority}
                        </span>
                        <span className="text-[10px] uppercase tracking-[0.22em] px-2 py-1 border border-[#1A422D] text-[#1A422D]">
                          {t.status.replace("_", " ")}
                        </span>
                      </div>
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      </main>
      <SupportSubmitModal open={showNew} onClose={() => setShowNew(false)} onSubmitted={() => { setShowNew(false); load(); }} />
    </>
  );
}
